import { RecordCollection } from './record-collection.js';
import { ListFilterBar } from './data-view.js';
import { SegmentedControl, IconButton } from '@hue/design-system';
import {
  Button,
  Card,
  Input,
  Modal,
  MultiSelect,
  StatusPill,
  Textarea,
  useSnackbar,
} from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Plus, ShieldCheck, UserRoundCog, PencilLine, Archive } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  getApiError,
  platformAccessApi,
  type PlatformAccess,
  type PlatformRole,
} from '../lib/api.js';
import type { PlatformAccessView } from './list-views.js';
import type { ViewSetter } from './view-state.js';
import { PageHeader } from './page-title.js';

export function ConsolePlatformAccess({
  view,
  onView,
  canManage,
}: {
  view: PlatformAccessView;
  onView: ViewSetter<PlatformAccessView>;
  canManage: boolean;
}) {
  const roles = useQuery({
    queryKey: ['platform', 'roles'],
    queryFn: platformAccessApi.roles,
    retry: false,
  });
  const accesses = useQuery({
    queryKey: ['platform', 'accesses'],
    queryFn: () => platformAccessApi.accesses(),
    retry: false,
  });
  const permissions = useQuery({
    queryKey: ['platform', 'permissions'],
    queryFn: platformAccessApi.permissions,
    retry: false,
    enabled: canManage,
  });
  const term = view.search.trim().toLocaleLowerCase('pt-BR');
  const visibleAccesses = (accesses.data ?? []).filter(
    (access) =>
      (view.activity === 'ALL' ||
        (view.activity === 'ACTIVE' && access.active) ||
        (view.activity === 'INACTIVE' && !access.active)) &&
      (term === '' ||
        `${access.firstName} ${access.lastName}`.toLocaleLowerCase('pt-BR').includes(term) ||
        access.email.toLocaleLowerCase('pt-BR').includes(term) ||
        access.roles.some((role) => role.displayName.toLocaleLowerCase('pt-BR').includes(term))),
  );
  const visibleRoles = (roles.data ?? []).filter(
    (role) =>
      (view.activity === 'ALL' ||
        (view.activity === 'ACTIVE' && role.active) ||
        (view.activity === 'INACTIVE' && !role.active)) &&
      (term === '' ||
        role.displayName.toLocaleLowerCase('pt-BR').includes(term) ||
        role.key.toLocaleLowerCase('pt-BR').includes(term) ||
        (role.description ?? '').toLocaleLowerCase('pt-BR').includes(term)),
  );

  return (
    <div>
      <PageHeader
        trail="Plataforma / Acessos"
        title="Acessos da plataforma"
        description="Papéis globais separados dos vínculos de empresa. Toda concessão exige MFA ativo e é auditada."
        actions={
          canManage ? (
            view.tab === 'accesses' ? (
              <AccessEditorTrigger roles={roles.data ?? []} />
            ) : (
              <RoleEditorTrigger permissions={permissions.data ?? []} />
            )
          ) : null
        }
      />

      <div className="mb-5 flex border-b border-surface-border" role="tablist" aria-label="Acessos">
        {(
          [
            ['accesses', 'Pessoas'],
            ['roles', 'Papéis'],
          ] as const
        ).map(([tab, label]) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={view.tab === tab}
            onClick={() => onView({ tab, search: '', activity: 'ALL' })}
            className={`border-b-2 px-4 py-3 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 ${
              view.tab === tab
                ? 'border-brand-400 text-brand-400'
                : 'border-transparent text-content-muted hover:text-content'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <ListFilterBar
        searchLabel={view.tab === 'accesses' ? 'Buscar pessoa' : 'Buscar papel'}
        searchPlaceholder={
          view.tab === 'accesses' ? 'Nome, e-mail ou papel' : 'Nome, chave ou descrição'
        }
        search={view.search}
        onSearchChange={(search) => onView({ search }, { replace: true })}
        count={
          view.tab === 'accesses'
            ? `${visibleAccesses.length} ${visibleAccesses.length === 1 ? 'pessoa' : 'pessoas'}`
            : `${visibleRoles.length} ${visibleRoles.length === 1 ? 'papel' : 'papéis'}`
        }
      >
        <SegmentedControl
          label="Atividade"
          compact
          value={view.activity}
          onChange={(activity) => onView({ activity })}
          options={[
            { value: 'ALL', label: 'Todos' },
            { value: 'ACTIVE', label: 'Ativos' },
            { value: 'INACTIVE', label: 'Inativos' },
          ]}
        />
      </ListFilterBar>

      {view.tab === 'accesses' ? (
        <AccessList
          entries={visibleAccesses}
          roles={roles.data ?? []}
          loading={accesses.isLoading || roles.isLoading}
          error={accesses.error ?? roles.error}
          canManage={canManage}
        />
      ) : (
        <RoleList
          entries={visibleRoles}
          permissions={permissions.data ?? []}
          loading={roles.isLoading}
          error={roles.error}
          canManage={canManage}
        />
      )}
    </div>
  );
}

function AccessList({
  entries,
  roles,
  loading,
  error,
  canManage,
}: {
  entries: PlatformAccess[];
  roles: PlatformRole[];
  loading: boolean;
  error: unknown;
  canManage: boolean;
}) {
  if (loading) return <StateCard message="Carregando acessos…" />;
  if (error) return <StateCard message={getApiError(error)} danger />;
  if (entries.length === 0) return <StateCard message="Nenhum acesso encontrado." />;
  return (
    <RecordCollection
      label="Pessoas da plataforma"
      records={entries}
      recordKey={(access) => access.id}
      columns={[
        {
          key: 'name',
          label: 'Pessoa',
          value: (access) => `${access.firstName} ${access.lastName}`,
          render: (access) => (
            <strong>
              {access.firstName} {access.lastName}
            </strong>
          ),
        },
        {
          key: 'email',
          label: 'E-mail',
          value: (access) => access.email,
          render: (access) => access.email,
        },
        {
          key: 'roles',
          label: 'Papéis',
          render: (access) =>
            access.roles.map((role) => role.displayName).join(', ') || 'Sem papel',
        },
        {
          key: 'status',
          label: 'Situação',
          value: (access) => Number(access.active),
          render: (access) => (
            <StatusPill tone={access.active ? 'positive' : 'neutral'}>
              {access.active ? 'Ativo' : 'Inativo'}
            </StatusPill>
          ),
        },
      ]}
      actions={(access) => (canManage ? <AccessActions access={access} roles={roles} /> : null)}
      card={(access) => (
        <article key={access.id} className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center">
          <span className="grid size-11 shrink-0 place-items-center rounded-full bg-brand-500/12 text-brand-400">
            <UserRoundCog className="size-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-semibold">
                {access.firstName} {access.lastName}
              </h2>
              <StatusPill tone={access.active ? 'positive' : 'neutral'}>
                {access.active ? 'Ativo' : 'Inativo'}
              </StatusPill>
            </div>
            <p className="mt-1 truncate text-sm text-content-muted">{access.email}</p>
            <p className="mt-1 text-xs text-content-faint">
              {access.roles.map((role) => role.displayName).join(', ') || 'Sem papel'}
            </p>
          </div>
          {canManage ? <AccessActions access={access} roles={roles} /> : null}
        </article>
      )}
    />
  );
}

function AccessEditorTrigger({ roles }: { roles: PlatformRole[] }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)}>
        <Plus className="size-4" /> Conceder acesso
      </Button>
      <AccessEditor open={open} onOpenChange={setOpen} roles={roles} />
    </>
  );
}

function AccessActions({ access, roles }: { access: PlatformAccess; roles: PlatformRole[] }) {
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  return (
    <div className="flex gap-1">
      <IconButton label="Editar" icon={<PencilLine />} onClick={() => setEditing(true)} />
      {access.active ? (
        <IconButton
          label="Desativar"
          icon={<Archive />}
          tone="danger"
          onClick={() => setRemoving(true)}
        />
      ) : null}
      <AccessEditor open={editing} onOpenChange={setEditing} roles={roles} access={access} />
      <DeactivateAccess open={removing} onOpenChange={setRemoving} access={access} />
    </div>
  );
}

function AccessEditor({
  open,
  onOpenChange,
  roles,
  access,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roles: PlatformRole[];
  access?: PlatformAccess | undefined;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [email, setEmail] = useState(access?.email ?? '');
  const [roleIds, setRoleIds] = useState(access?.roles.map((role) => role.id) ?? []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  function changeOpen(next: boolean) {
    if (next) {
      setEmail(access?.email ?? '');
      setRoleIds(access?.roles.map((role) => role.id) ?? []);
      setError(undefined);
    }
    onOpenChange(next);
  }

  async function save() {
    if (!email.trim() || roleIds.length === 0) {
      setError('Informe o e-mail e selecione ao menos um papel.');
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      await platformAccessApi.replaceRoles({ email: email.trim(), roleIds });
      await queryClient.invalidateQueries({ queryKey: ['platform', 'accesses'] });
      await queryClient.invalidateQueries({ queryKey: ['platform', 'roles'] });
      onOpenChange(false);
      snackbar.success(access ? 'Papéis atualizados.' : 'Acesso concedido.');
    } catch (caught) {
      setError(getApiError(caught));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={changeOpen}
      title={access ? 'Editar acesso da plataforma' : 'Conceder acesso à plataforma'}
      description="A identidade precisa existir e possuir MFA ativo."
    >
      <div className="space-y-5">
        <Input
          label="E-mail da identidade"
          type="email"
          autoComplete="email"
          value={email}
          disabled={Boolean(access)}
          onChange={(event) => setEmail(event.target.value)}
        />
        <MultiSelect
          label="Papéis"
          options={roles
            .filter((role) => role.active)
            .map((role) => ({ value: role.id, label: role.displayName }))}
          selected={roleIds}
          onChange={setRoleIds}
          itemNoun="papéis"
          searchPlaceholder="Buscar papel"
        />
        {error ? <RequestError message={error} /> : null}
        <div className="flex flex-col-reverse gap-3 border-t border-surface-border pt-5 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button loading={saving} onClick={() => void save()}>
            Salvar acesso
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function DeactivateAccess({
  open,
  onOpenChange,
  access,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  access: PlatformAccess;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  async function deactivate() {
    setSaving(true);
    setError(undefined);
    try {
      await platformAccessApi.deactivateAccess(access.userId);
      await queryClient.invalidateQueries({ queryKey: ['platform', 'accesses'] });
      await queryClient.invalidateQueries({ queryKey: ['platform', 'roles'] });
      onOpenChange(false);
      snackbar.success('Acesso desativado e sessões globais revogadas.');
    } catch (caught) {
      setError(getApiError(caught));
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal open={open} onOpenChange={onOpenChange} title="Desativar acesso da plataforma">
      <p className="text-sm leading-6 text-content-secondary">
        {access.firstName} perderá o console global. Seus vínculos com empresas não serão alterados.
      </p>
      {error ? <RequestError message={error} /> : null}
      <div className="mt-6 flex flex-col-reverse gap-3 border-t border-surface-border pt-5 sm:flex-row sm:justify-end">
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Cancelar
        </Button>
        <Button loading={saving} onClick={() => void deactivate()}>
          Desativar acesso
        </Button>
      </div>
    </Modal>
  );
}

function RoleList({
  entries,
  permissions,
  loading,
  error,
  canManage,
}: {
  entries: PlatformRole[];
  permissions: Array<{ key: string; category: string; displayName: string }>;
  loading: boolean;
  error: unknown;
  canManage: boolean;
}) {
  if (loading) return <StateCard message="Carregando papéis…" />;
  if (error) return <StateCard message={getApiError(error)} danger />;
  if (entries.length === 0) return <StateCard message="Nenhum papel encontrado." />;
  return (
    <RecordCollection
      label="Papéis da plataforma"
      records={entries}
      recordKey={(role) => role.id}
      columns={[
        {
          key: 'name',
          label: 'Papel',
          value: (role) => role.displayName,
          render: (role) => (
            <div>
              <strong>{role.displayName}</strong>
              <p className="text-xs text-content-muted">{role.key}</p>
            </div>
          ),
        },
        {
          key: 'description',
          label: 'Descrição',
          render: (role) => role.description ?? 'Papel personalizado da plataforma.',
        },
        {
          key: 'permissions',
          label: 'Permissões',
          value: (role) => role.permissionKeys.length,
          render: (role) => role.permissionKeys.length,
        },
        {
          key: 'people',
          label: 'Pessoas',
          value: (role) => role.userCount,
          render: (role) => role.userCount,
        },
        {
          key: 'status',
          label: 'Situação',
          value: (role) => Number(role.active),
          render: (role) => (
            <div>
              <StatusPill tone={role.active ? 'positive' : 'neutral'}>
                {role.active ? 'Ativo' : 'Inativo'}
              </StatusPill>
              {role.protected ? <p className="text-xs">Protegido</p> : null}
            </div>
          ),
        },
      ]}
      actions={(role) =>
        canManage && !role.protected && role.active ? (
          <RoleActions role={role} permissions={permissions} />
        ) : null
      }
      card={(role) => (
        <Card className="h-full p-5">
          <div className="flex items-start gap-3">
            <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-brand-500/12 text-brand-400">
              {role.protected ? (
                <ShieldCheck className="size-5" />
              ) : (
                <KeyRound className="size-5" />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="font-semibold">{role.displayName}</h2>
                <StatusPill tone={role.active ? 'positive' : 'neutral'}>
                  {role.active ? 'Ativo' : 'Inativo'}
                </StatusPill>
              </div>
              <p className="mt-1 font-mono text-xs text-content-faint">{role.key}</p>
            </div>
          </div>
          <p className="hue-list-description mt-4 text-sm leading-6 text-content-muted">
            {role.description ?? 'Papel personalizado da plataforma.'}
          </p>
          <p className="mt-3 text-xs text-content-faint">
            {String(role.permissionKeys.length)} permissões · {String(role.userCount)} pessoas
          </p>
          {canManage && !role.protected && role.active ? (
            <RoleActions role={role} permissions={permissions} />
          ) : null}
        </Card>
      )}
    />
  );
}

function RoleEditorTrigger({
  permissions,
}: {
  permissions: Array<{ key: string; category: string; displayName: string }>;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)}>
        <Plus className="size-4" /> Criar papel
      </Button>
      <RoleEditor open={open} onOpenChange={setOpen} permissions={permissions} />
    </>
  );
}

function RoleActions({
  role,
  permissions,
}: {
  role: PlatformRole;
  permissions: Array<{ key: string; category: string; displayName: string }>;
}) {
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  return (
    <div className="mt-5 flex gap-1 border-t border-surface-border pt-4">
      <IconButton label="Editar" icon={<PencilLine />} onClick={() => setEditing(true)} />
      <IconButton
        label="Desativar"
        icon={<Archive />}
        tone="danger"
        onClick={() => setRemoving(true)}
      />
      <RoleEditor open={editing} onOpenChange={setEditing} permissions={permissions} role={role} />
      <DeactivateRole open={removing} onOpenChange={setRemoving} role={role} />
    </div>
  );
}

function RoleEditor({
  open,
  onOpenChange,
  permissions,
  role,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  permissions: Array<{ key: string; category: string; displayName: string }>;
  role?: PlatformRole | undefined;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [displayName, setDisplayName] = useState(role?.displayName ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [permissionKeys, setPermissionKeys] = useState(role?.permissionKeys ?? []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const options = useMemo(
    () =>
      permissions.map((item) => ({
        value: item.key,
        label: `${item.category} — ${item.displayName}`,
      })),
    [permissions],
  );

  function changeOpen(next: boolean) {
    if (next) {
      setDisplayName(role?.displayName ?? '');
      setDescription(role?.description ?? '');
      setPermissionKeys(role?.permissionKeys ?? []);
      setError(undefined);
    }
    onOpenChange(next);
  }

  async function save() {
    if (displayName.trim().length < 2 || permissionKeys.length === 0) {
      setError('Informe o nome e selecione ao menos uma permissão.');
      return;
    }
    setSaving(true);
    setError(undefined);
    const input = {
      displayName: displayName.trim(),
      description: description.trim() || null,
      permissionKeys,
    };
    try {
      if (role) await platformAccessApi.updateRole(role.id, input);
      else await platformAccessApi.createRole(input);
      await queryClient.invalidateQueries({ queryKey: ['platform', 'roles'] });
      onOpenChange(false);
      snackbar.success(role ? 'Papel atualizado.' : 'Papel criado.');
    } catch (caught) {
      setError(getApiError(caught));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={changeOpen}
      title={role ? 'Editar papel da plataforma' : 'Criar papel da plataforma'}
    >
      <div className="space-y-5">
        <Input
          label="Nome"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
        />
        <Textarea
          label="Descrição"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
        <MultiSelect
          label="Permissões"
          options={options}
          selected={permissionKeys}
          onChange={setPermissionKeys}
          itemNoun="permissões"
          searchPlaceholder="Buscar permissão"
        />
        {error ? <RequestError message={error} /> : null}
        <div className="flex flex-col-reverse gap-3 border-t border-surface-border pt-5 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button loading={saving} onClick={() => void save()}>
            Salvar papel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function DeactivateRole({
  open,
  onOpenChange,
  role,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role: PlatformRole;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  async function deactivate() {
    setSaving(true);
    setError(undefined);
    try {
      await platformAccessApi.deactivateRole(role.id);
      await queryClient.invalidateQueries({ queryKey: ['platform', 'roles'] });
      onOpenChange(false);
      snackbar.success('Papel desativado.');
    } catch (caught) {
      setError(getApiError(caught));
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal open={open} onOpenChange={onOpenChange} title="Desativar papel da plataforma">
      <p className="text-sm leading-6 text-content-secondary">
        O papel {role.displayName} só pode ser desativado sem atribuições ativas.
      </p>
      {error ? <RequestError message={error} /> : null}
      <div className="mt-6 flex flex-col-reverse gap-3 border-t border-surface-border pt-5 sm:flex-row sm:justify-end">
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Cancelar
        </Button>
        <Button loading={saving} onClick={() => void deactivate()}>
          Desativar papel
        </Button>
      </div>
    </Modal>
  );
}

function StateCard({ message, danger = false }: { message: string; danger?: boolean }) {
  return (
    <Card className="p-8 text-center">
      <p className={danger ? 'text-sm text-danger' : 'text-sm text-content-muted'}>{message}</p>
    </Card>
  );
}

function RequestError({ message }: { message: string }) {
  return (
    <p
      role="alert"
      className="mt-4 rounded-xl border border-danger/30 bg-danger/10 p-3 text-sm text-danger"
    >
      {message}
    </p>
  );
}
